import { LitElement, html, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';
import styles from './styles.js';

export type ToggleSwitchSize = 'xs' | 'small' | 'medium' | 'large';

/**
 * uwc-toggleswitch
 *
 * Toggle switch component with W3C form association and constraint validation.
 * Set `required` to enforce that the switch must be turned on.
 *
 * @attr checked  - Whether the switch is on.
 * @attr label    - Label text rendered beside the switch.
 * @attr size     - xs | small | medium | large. Default: xs.
 * @attr required - The switch must be on (checked).
 * @attr disabled - Disable the switch.
 * @attr invalid  - Mark as invalid.
 * @attr name     - Name for form submission.
 *
 * @slot default - Custom label content (overrides label attr).
 *
 * @fires uwc-change  - Fired when the switch is toggled. Detail: { checked }.
 * @fires uwc-invalid - Fired when required but off. Detail: { validity, validationMessage }.
 */
export class UwcToggleSwitch extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Core ─────────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) checked  = false;
  @property()                                 label?:   string;
  @property()                                 name?:    string;

  // ── Modifiers ─────────────────────────────────────────────────────

  @property({ reflect: true })                size: ToggleSwitchSize = 'xs';
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;

  override firstUpdated() {
    const input = this.shadowRoot?.querySelector('input') ?? null;
    this._validation.setAnchor(input);
    this._validation.syncFormValue(this.checked ? 'on' : null);
    this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────

  private _onChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.checked = input.checked;
    this._validation.handleChange(this.checked ? 'on' : '', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(this.checked ? 'on' : null);
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { checked: this.checked, originalEvent: e },
    }));
  }

  // ── Render ────────────────────────────────────────────────────────

  override render() {
    const labelContent = this.label
      ? html`<span part="label" class="uwc-ts__label">${this.label}</span>`
      : html`<slot part="label" class="uwc-ts__label"></slot>`;

    return html`
      <label style="display:inline-flex;align-items:center;gap:0.5rem;cursor:${this.disabled ? 'not-allowed' : 'pointer'}">
        <span part="track" class="uwc-ts" role="switch" aria-checked=${this.checked}>
          <input
            type="checkbox"
            .checked=${this.checked}
            name=${ifDefined(this.name)}
            ?disabled=${this.disabled}
            ?required=${this.required}
            @change=${this._onChange}
          />
          <span part="dot" class="uwc-ts__dot"></span>
        </span>
        ${labelContent}
      </label>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-toggleswitch': UwcToggleSwitch; }
}
